import RevealStagger from "@/components/common/RevealStagger";
import Button from "@/components/common/Button";
import Sparkline from "@/components/visual/Sparkline";
import { ArrowRight } from "@/components/common/icons";
import { ASSET_CATEGORY_CARDS } from "@/lib/data";
import { useLocale } from "@/i18n/useLocale";
import { landingAssetsFor } from "@/content/landing";
import { pathFor } from "@/routes/manifest";

export default function AssetStrip(): JSX.Element {
  const locale = useLocale();
  const t = landingAssetsFor(locale);

  return (
    <section className="section-y-lg bg-bg-2 border-y border-border" style={{ paddingTop: 64, paddingBottom: 64 }}>
      <div className="container-page">
        <div className="flex justify-between items-center gap-6 flex-wrap mb-6">
          <div>
            <h3 className="text-h3 font-display font-semibold" style={{ fontSize: 22 }}>
              {t.title.lead}
              <span className="text-brand-gradient">{t.title.highlight}</span>
            </h3>
          </div>
          <Button variant="ghost" href={pathFor("/tradable-assets", locale)}>
            {t.ctaFullList} <ArrowRight />
          </Button>
        </div>

        {/*
         * Mobile: 2-col grid; with 5 cards the trailing card spans
         * both columns so the row reads 2 + 2 + 1-wide. Above md
         * the original auto-fit behaviour returns via grid-cols
         * stepping up to 3/5.
         */}
        <RevealStagger
          className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
          step={80}
        >
          {ASSET_CATEGORY_CARDS.map((c, i) => {
            const isLastOdd =
              i === ASSET_CATEGORY_CARDS.length - 1 &&
              ASSET_CATEGORY_CARDS.length % 2 === 1;
            return (
              <div
                key={c.key}
                className={`glass-card glass-card-hover h-full p-3.5 md:p-[18px] ${
                  isLastOdd ? "col-span-2 md:col-span-1" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10.5px] text-brand-emphasis tracking-[0.14em]">{c.num}</div>
                  <span
                    className="font-mono text-[9px] tracking-[0.1em] font-bold rounded"
                    style={{
                      padding: "3px 7px",
                      background: c.up
                        ? "rgb(var(--success) / 0.12)"
                        : "rgb(var(--danger) / 0.12)",
                      color: c.up ? "rgb(var(--success))" : "rgb(var(--danger))",
                    }}
                  >
                    {c.up ? t.up : t.down}
                  </span>
                </div>
                <div
                  className="font-display tracking-tight mt-2 text-[20px] md:text-[24px]"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {t.categories[c.key].name}
                </div>
                <div className="font-mono text-fg-muted text-[10.5px] md:text-[11px] mt-1">
                  {c.count} {t.categories[c.key].countUnit} · {t.categories[c.key].description}
                </div>
                <div className="mt-3 md:mt-3.5 pt-3 md:pt-3.5 border-t border-border flex justify-between items-center gap-2">
                  <span className="font-mono text-[11px] md:text-[11.5px] font-bold truncate">
                    {c.leaderSymbol}
                  </span>
                  <Sparkline
                    values={[...c.leaderSeries]}
                    color={c.up ? "rgb(var(--success))" : "rgb(var(--danger))"}
                    width={48}
                    height={20}
                  />
                </div>
              </div>
            );
          })}
        </RevealStagger>
      </div>
    </section>
  );
}
